import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory } from 'react-router';
import '@microsoft/applicationinsights-shims';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
  ApplicationInsights,
  type IApplicationInsights,
  type ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import useCurrentUser from 'infrastructure/currentUser/useCurrentUser';
import useCurrentTenant from 'infrastructure/currentTenant/useCurrentTenant';
import useAxios from 'infrastructure/axios/useAxios';
import {
  authTokenRefreshEndpoint,
  menuEndpoint,
} from 'infrastructure/api/endpoints';
import { getSettings as getSettingsEndpoint } from 'infrastructure/api/endpoints/settings';
import buildApiErrorTelemetryProperties from './apiErrorTelemetry';

type AppInsightsCustomProperties = {
  [key: string]: any;
};

type AppInsightContextValue = {
  trackAppInsightsError: (
    exception: Error | string,
    customProperties?: AppInsightsCustomProperties
  ) => void;
  trackAppInsightsEvent: (
    eventName: string,
    customProperties?: AppInsightsCustomProperties
  ) => void;
};

export const AppInsightsContext = createContext<AppInsightContextValue>({
  trackAppInsightsError: () => {},
  trackAppInsightsEvent: () => {},
});

export const useAppInsights = () => useContext(AppInsightsContext);

export const AppInsightsContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children,
}) => {
  const [appInsights, setAppInsights] = useState<IApplicationInsights | null>(
    null
  );
  const { currentUser } = useCurrentUser();
  const { currentTenant } = useCurrentTenant();
  const history = useHistory();
  const axiosInstance = useAxios();

  useEffect(() => {
    if (!currentUser || !process.env.APPINSIGHTS_INSTRUMENTATION_KEY) {
      return;
    }

    const appInsightsKey = process.env.APPINSIGHTS_INSTRUMENTATION_KEY;

    const telemetryInitializer = (envelope: ITelemetryItem) => {
      if (envelope.data && currentUser) {
        // eslint-disable-next-line no-param-reassign
        envelope.data = {
          ...envelope.data,
          UserId: currentUser.id,
          UserName: currentUser.name,
          UserRole: currentUser.roles?.[0],
        };
      }
    };

    if (appInsightsKey) {
      const reactPlugin = new ReactPlugin();
      const appInsightsInstance = new ApplicationInsights({
        config: {
          instrumentationKey: appInsightsKey,
          /**
           * Ignoring this error - versions are matching.
           * @see https://github.com/microsoft/applicationinsights-react-js?tab=readme-ov-file#compatibility-matrix
           * */
          /** @ts-ignore */
          extensions: [reactPlugin],
          extensionConfig: {
            [reactPlugin.identifier]: { history },
          },
        },
      });

      appInsightsInstance.loadAppInsights();
      appInsightsInstance.addTelemetryInitializer(telemetryInitializer);
      setAppInsights(appInsightsInstance);
    }
  }, [currentUser, history]);

  const trackAppInsightsError = useCallback(
    (
      error: Error | string,
      customProperties?: {
        [key: string]: any;
      }
    ) => {
      if (!currentTenant || !appInsights) {
        return;
      }

      const exception = error instanceof Error ? error : new Error(error);
      const properties = {
        ...customProperties,
        Tenant: currentTenant.name,
      };

      appInsights.trackException({
        exception,
        properties,
      });

      appInsights.trackEvent({
        name: exception.message,
        properties,
      });
    },
    [appInsights, currentTenant]
  );

  const trackAppInsightsEvent = useCallback(
    (
      eventName: string,
      customProperties?: {
        [key: string]: any;
      }
    ) => {
      if (!currentTenant || !appInsights) {
        return;
      }

      appInsights.trackEvent({
        name: eventName,
        properties: {
          ...customProperties,
          Tenant: currentTenant.name,
        },
      });
    },
    [appInsights, currentTenant]
  );

  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.response.use(
      response => response,
      error => {
        try {
          const is401 = error?.response?.status === 401;
          const requestPathname = new URL(error.config?.url)?.pathname || '';
          const isProbablyRefreshTokenError =
            // The following endpoints are hit before the user is redirected to the login page
            requestPathname.endsWith(menuEndpoint) ||
            requestPathname.endsWith(getSettingsEndpoint) ||
            requestPathname.endsWith(authTokenRefreshEndpoint);

          const appInsightsProperties = buildApiErrorTelemetryProperties(error);

          // If the errors are likely to be from the auth token expiring, we don't want to flood AppInsights
          // and the #on-call channel with errors that likely aren't actual errors.
          // However we should still log these in case they are actual errors and we need them for debugging
          if (is401 && isProbablyRefreshTokenError) {
            trackAppInsightsEvent(
              `CMS: API Response error`,
              appInsightsProperties
            );
          } else {
            trackAppInsightsError(
              `CMS: API Response error`,
              appInsightsProperties
            );
          }
        } catch (err) {
          console.error(err);
        }

        // Always re-throw the error so other error handlers can process it
        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorId) {
        axiosInstance.interceptors.response.eject(interceptorId);
      }
    };
  }, [axiosInstance, trackAppInsightsError, trackAppInsightsEvent]);

  const contextValue = useMemo(
    () => ({ trackAppInsightsError, trackAppInsightsEvent }),
    [trackAppInsightsError, trackAppInsightsEvent]
  );

  return (
    <AppInsightsContext.Provider value={contextValue}>
      {children}
    </AppInsightsContext.Provider>
  );
};
